import { Injectable, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { ZonePageUrlProvider } from '@app/finxone-web-frontend/app/Modules/zones/zone-url-provider';
import { FormID } from '@finxone-platform/form-action';
import { ACCOUNTTYPE, FormFieldConfig } from '@finxone-platform/shared/sys-config-types';
import { Observable, of } from 'rxjs';

@Injectable()
export class MockWorkflowFormsService {
  private forms = new Map<string, FormGroup>();
  private arrayEditIndices = new Map<string, number | null>();
  public currentPageFormsValid = signal<boolean>(true);
  public formProgressDataAssigned = signal<boolean>(false);

  constructor() {
    this.createForm(FormID.BENEFICIARY_FORM, []);
    this.createForm(FormID.ONBOARDING_FLOW, []);
    // add the forms you want to mock here.
  }

  getFormPart(path: string): FormGroup | undefined {
    return this.forms.get(path);
  }

  createForm(path: string, config: FormFieldConfig[], updateCurrentFormValue = false): void {
    const basePath = path.split('.')?.[0];
    if (path === FormID.BENEFICIARY_FORM) {
      // Hardcoded sample data for BeneficiaryFormType
      const form = new FormGroup({
        type: new FormControl(ACCOUNTTYPE.PERSONAL),
        country: new FormControl('GB'),
        currency: new FormControl('GBP'),
        paymentScheme: new FormControl('Local'),
        paymentRail: new FormControl('FPS'),
        accountDetails: new FormGroup({
          nickName: new FormControl('Johns Savings'),
          fullName: new FormControl('John Doe'),
          accountNumber: new FormControl('12345678'),
          sortCode: new FormControl('123456'),
          iban: new FormControl('GB29NWBK60161331926819'),
          BIC: new FormControl('NWBKGB2L'),
          aba: new FormControl(''),
          rtnCanada: new FormControl(''),
          clabe: new FormControl(''),
          bsbCode: new FormControl(''),
          ifsc: new FormControl(''),
          cnaps: new FormControl(''),
          bankId: new FormControl('001'),
          bankName: new FormControl('NatWest'),
        }),
        beneficiaryAddress: new FormGroup({
          addressLine1: new FormControl('10 Downing St'),
          addressLine2: new FormControl(''),
          addressLine3: new FormControl(''),
          addressLine4: new FormControl(''),
          houseNumber: new FormControl('10'),
          refinement: new FormControl(''),
          city: new FormControl('London'),
          stateProvinceRegion: new FormControl('Greater London'),
          postalCode: new FormControl('SW1A 2AA'),
          country: new FormControl('GB'),
        }),
      });
      this.forms.set(path, form);
    }
    if (basePath === FormID.ONBOARDING_FLOW) {
      const controls: { [key: string]: AbstractControl } = {
        firstName: new FormControl(''),
        middleName: new FormControl(''),
        lastName: new FormControl(''),
        dob: new FormControl(''),
        country: new FormControl(''),
        county: new FormControl(''),
        city: new FormControl(''),
        address1: new FormControl(''),
        address2: new FormControl(''),
        address3: new FormControl(''),
        address4: new FormControl(''),
        postalCode: new FormControl(''),
        password: new FormControl(''),
        confirm_password: new FormControl(''),
        email: new FormControl(''),
        phoneNumber: new FormControl(''),
        countryCode: new FormControl(''),
      };

      for (const field of config) {
        if (!controls[field.name]) {
          controls[field.name] = new FormControl('');
        }
      }

      const form = new FormGroup(controls);
      this.forms.set(path, form);
      return;
    }

    // Generic fallback for any other path (e.g. group/array-nested Form Widgets): build a plain
    // FormGroup from config so getFormPart resolves, reusing an existing one if already created.
    if (!this.forms.has(path)) {
      this.forms.set(path, new FormGroup({}));
    }
    this.addFormControl(this.forms.get(path)!, config, updateCurrentFormValue);
  }

  /**
   * Mock counterpart of WorkflowFormsService.resolveArrayItemFormPath - just enough to let
   * 'array' typed Form Widgets render in the builder's preview without crashing. Uses a local
   * index map (no NGXS store in this mock) instead of persisted array-edit-index state.
   */
  resolveArrayItemFormPath(
    rootFormID: string,
    childKey: string,
    zonePageUrlProvider?: ZonePageUrlProvider,
  ): { path: string; index: number } {
    const arrayStateKey = `${rootFormID}.${childKey}`;
    const parentFormPath = this.generateCustomFormId(rootFormID, zonePageUrlProvider);
    if (!this.getFormPart(parentFormPath)) {
      this.createForm(parentFormPath, []);
    }

    const index = this.getActiveFormArrayIndex(arrayStateKey) ?? 0;
    this.setActiveFormArrayIndex(arrayStateKey, index);

    const path = `${parentFormPath}.${childKey}.${index}`;
    if (!this.getFormPart(path)) {
      this.createForm(path, []);
    }
    return { path, index };
  }

  getActiveFormArrayIndex(arrayStateKey: string): number | null {
    return this.arrayEditIndices.get(arrayStateKey) ?? null;
  }

  setActiveFormArrayIndex(arrayStateKey: string, index: number | null): void {
    this.arrayEditIndices.set(arrayStateKey, index);
  }

  trackFormValidity(path: string, form: FormGroup): void {
    // No-op for mock
  }

  // Create nested formGroup based on pageUrl
  public generateCustomFormId(path: string, zonePageUrlProvider?: ZonePageUrlProvider): string {
    const currentPageUrl = zonePageUrlProvider?.pageUrlInput;
    return `${path}.customForm-${currentPageUrl}`;
  }

  addFormControl(currentForm: FormGroup, config: FormFieldConfig[], updateCurrentFormValue = false): void {
    for (const field of config) {
      if (!field.name) continue;
      if (!currentForm.contains(field.name)) {
        currentForm.addControl(field.name, new FormControl(field.defaultValue ?? ''));
      } else if (updateCurrentFormValue) {
        currentForm.get(field.name)?.setValue(field.defaultValue ?? '');
      }
    }
  }

  clearAllForms(): void {
    this.forms.clear();
  }

  observeForm(path: string): Observable<FormGroup | undefined> {
    return of(this.forms.get(path));
  }

  patchFormValues(path: string, values: any): void {
    const form = this.forms.get(path);
    if (form) {
      form.patchValue(values);
    }
  }

  isFormValid(path: string): boolean {
    const form = this.forms.get(path);
    return form ? form.valid : true;
  }

  getFormErrors(path: string): { [key: string]: any } {
    const form = this.forms.get(path);
    return form ? form.errors ?? {} : {};
  }

  resetForm(path: string): void {
    const form = this.forms.get(path);
    if (form) {
      form.reset();
    }
  }

  getFormValues(path: string): any {
    const form = this.forms.get(path);
    return form ? form.value : {};
  }

  markFormTouched(path: string): void {
    const form = this.forms.get(path);
    if (form) {
      form.markAllAsTouched();
    }
  }

  registerCurrentPageForms(formPaths: string[]): void {
    // No-op for mock
  }

  registerCurrentPageFormWidgetForm(): void {
    // No-op for mock
  }

  getNestedFormDispatchMeta(): undefined {
    // No-op for mock - kept in sync with WorkflowFormsService's contract so FormButtonWidgetComponent
    // doesn't throw if this mock is ever substituted where it actually reaches the dispatch loop.
    return undefined;
  }

  clearCurrentPageForms(): void {
    // No-op for mock
  }

  getCurrentPageForms(): string[] {
    return [];
  }

  validateCurrentPageForms(): {
    isValid: boolean;
    invalidForms: { path: string; errors: any }[];
  } {
    return { isValid: true, invalidForms: [] };
  }

  markCurrentPageFormsTouched(): void {
    // No-op for mock
  }

  addValidatorsToControl(path: string, controlName: string, validators: any[]): void {
    // No-op for mock
  }

  getAllFormValuesFlattened(): { [key: string]: any } {
    const allValues: { [key: string]: any } = {};

    const formValues = {
      amount: 100,
      description: 'Test description',
    };
    Object.assign(allValues, formValues);

    return allValues;
  }

  public observeAllFormsValuesFlattened(): Observable<{ [key: string]: any }> {
    return of(this.getAllFormValuesFlattened());
  }
}
